import { Component, Inject, Vue, Watch } from 'vue-property-decorator';

import { namespace } from 'vuex-class';
import BriefingService from '@/service/briefing.service';
import type { IResourceModel } from '@/shared/model/briefing-widget.model';
import { convertToBriefingResourceReq } from '@/util/ds-util';
import { removeUnUsedSpace } from '@/util/string.util';
import { LoadingDataReportCard, ToastReportData } from '@/shared/model/briefing-resource.model';
import { LOADING_DATA_TYPE, REPORT_STYLES, REPORT_TOAST_STATUS, REPORT_TYPES } from '@/constants';
import { handleRemoveLocalStorageGoOverAgainData } from '@/util/localStorage-util';
import LoadingReportCardUtil from '@/util/loading-report-card.util';
import ReportToastUtil from '@/util/report-toast.util';

const appStoreState = namespace('appStore');
const briefingStore = namespace('briefingStore');

@Component({
  mixins: [LoadingReportCardUtil, ReportToastUtil],
  computed: {
    REPORT_STYLES() {
      return REPORT_STYLES;
    },
    REPORT_TYPES() {
      return REPORT_TYPES;
    },
  },
})
export default class PopupReportTypeSettingComponent extends Vue {
  @Inject('briefingService')
  private briefingService: () => BriefingService;

  @briefingStore.Getter
  public getSelectedResource!: IResourceModel;

  @appStoreState.Getter
  public getIsShowReportSettingReportType!: boolean;

  @appStoreState.Mutation
  public setIsShowReportSettingReportType!: (payload?: boolean) => void;

  @briefingStore.Getter
  public getStoreReferenceDocumentsForReport!: Array<any>;

  @briefingStore.Getter
  getReferenceDocuments!: Array<any>;

  @briefingStore.Getter
  public getSelectedBriefingPage!: any;

  @briefingStore.Getter
  public getIsSelectReportNoBriefingTab!: boolean;

  @briefingStore.Mutation
  public setIsSelectReportNoBriefingTab!: (isSelectReportNoBriefingTab: boolean) => void;

  @appStoreState.Getter
  public isShowDetailReport!: boolean;

  @appStoreState.Mutation
  public setShowDetailReport!: (b: boolean) => void;

  @briefingStore.Getter
  public getBriefingPages!: any[];

  @briefingStore.Mutation
  public setBriefingPages: (payload: any) => void;

  @briefingStore.Mutation
  public setSelectedBriefingPage: (payload: any) => void;

  @appStoreState.Mutation
  public setIsShowSuccessUpdateBriefing!: (payload: any) => void;

  @briefingStore.Getter
  getIsAllowGoOverAgain!: boolean;

  @briefingStore.Mutation
  public setIsAllowGoOverAgain!: (payload: boolean) => void;

  @appStoreState.Getter
  public getToastReportData!: any;

  @appStoreState.Mutation
  public setToastReportData!: (payload: any) => void;

  @briefingStore.Getter
  getWatchSetTempReferenceDocuments!: boolean;

  @briefingStore.Mutation
  public setWatchSetTempReferenceDocuments!: (payload: boolean) => void;

  loadingReportCardUtil = new LoadingReportCardUtil();
  reportToastUtil = new ReportToastUtil();

  isLoading = false;

  formData = {
    reportPattern: 'REPORT' as 'REPORT' | 'SOCIAL_MEDIA' | 'JOURNAL_NOTE',
    reportType: REPORT_TYPES.DAILY,
    reportStyle: {
      DAILY: REPORT_STYLES.BUSINESS,
      PRODUCT_REVIEW: REPORT_STYLES.BUSINESS,
      INVESTMENT: REPORT_STYLES.BUSINESS,
      INDEPTH_DAILY: REPORT_STYLES.BUSINESS,
      SOCIAL_MEDIA: REPORT_STYLES.BLOG,
      JOURNAL_NOTE: REPORT_STYLES.BUSINESS,
    },
    updateOptions: 'UPDATE' as 'CREATE' | 'UPDATE',
  };

  isForReportDetail = false;

  public get userIdentity(): any {
    return this.$store.getters?.account
      ? this.$store.getters?.account
      : this.$store.getters?.userIdentity
      ? this.$store.getters?.userIdentity
      : JSON.parse(localStorage.getItem('localStorageUser'))
      ? JSON.parse(localStorage.getItem('localStorageUser'))
      : null;
  }

  mounted() {
    this.setData();
  }

  get userKey() {
    return this.userIdentity?.id || JSON.parse(localStorage.getItem('localStorageUser'))?.id;
  }

  get reportTypes() {
    return [
      this.formData.reportType +
        (this.formData.reportStyle[this.formData.reportType] === REPORT_STYLES.BUSINESS
          ? ''
          : `_${this.formData.reportStyle[this.formData.reportType]}`),
    ];
  }

  get account() {
    return this.$store.getters.account;
  }

  @Watch('getSelectedResource')
  onGetSelectedResourceChange() {
    this.setData();
  }

  get formValid() {
    return this.formData.reportType;
  }

  selectType(type: any) {
    // Toggle if type === typeSelected => set null (un-checked)
    this.formData.reportType = this.formData.reportType === type ? null : type;
  }

  setData() {
    this.formData.updateOptions = 'UPDATE';

    this.isForReportDetail =
      this.getSelectedResource &&
      (this.getSelectedResource?._id || this.getSelectedResource?.docId || this.getSelectedResource?.originParentDocDetail?._id) &&
      this.getStoreReferenceDocumentsForReport &&
      this.getStoreReferenceDocumentsForReport?.length > 0 &&
      this.isShowDetailReport;

    const reportType = this.isForReportDetail ? this.getSelectedResource?.reportType : this.getSelectedBriefingPage?.reportTypes;
    if (reportType) {
      if (reportType.includes(REPORT_TYPES.DAILY)) {
        this.formData.reportType = REPORT_TYPES.DAILY;
        this.formData.reportPattern = 'REPORT';
      } else if (reportType.includes(REPORT_TYPES.PRODUCT_REVIEW)) {
        this.formData.reportType = REPORT_TYPES.PRODUCT_REVIEW;
        this.formData.reportPattern = 'REPORT';
      } else if (reportType.includes(REPORT_TYPES.INVESTMENT)) {
        this.formData.reportType = REPORT_TYPES.INVESTMENT;
        this.formData.reportPattern = 'REPORT';
      } else if (reportType.includes(REPORT_TYPES.INDEPTH_DAILY)) {
        this.formData.reportType = REPORT_TYPES.INDEPTH_DAILY;
        this.formData.reportPattern = 'REPORT';
      } else if (reportType.includes(REPORT_TYPES.JOURNAL_NOTE)) {
        this.formData.reportType = REPORT_TYPES.JOURNAL_NOTE;
        this.formData.reportPattern = 'JOURNAL_NOTE';
      } else if (reportType.includes(REPORT_TYPES.SOCIAL_MEDIA)) {
        this.formData.reportType = REPORT_TYPES.SOCIAL_MEDIA;
        this.formData.reportPattern = 'SOCIAL_MEDIA';
      }

      if (reportType.includes(REPORT_STYLES.BLOG)) {
        this.formData.reportStyle[this.formData.reportType] = REPORT_STYLES.BLOG;
      } else if (reportType.includes(REPORT_STYLES.FACEBOOK)) {
        this.formData.reportStyle[this.formData.reportType] = REPORT_STYLES.FACEBOOK;
      } else if (reportType.includes(REPORT_STYLES.X)) {
        this.formData.reportStyle[this.formData.reportType] = REPORT_STYLES.X;
      } else if (reportType.includes(REPORT_STYLES.YOUTUBE)) {
        this.formData.reportStyle[this.formData.reportType] = REPORT_STYLES.YOUTUBE;
      }
    }
  }

  async callAPICreateNewReport() {
    if (
      !this.getStoreReferenceDocumentsForReport ||
      (this.getStoreReferenceDocumentsForReport && this.getStoreReferenceDocumentsForReport.length === 0)
    ) {
      return;
    }

    this.isLoading = true;

    const requestIdCreateReport = 'CR'.concat('_' + new Date().toISOString()).concat('_' + this.userKey) || new Date().getTime().toString();

    const requestedResourceList = this.getStoreReferenceDocumentsForReport
      .filter(item => item?.data?._id || item?.id || item?.docId)
      .map(item => item?.data?._id || item?.id || item?.docId);
    let documents = this.getStoreReferenceDocumentsForReport.map(item => ({
      ...item.data,
      parentDocId: item?.data?._id || item?.id || item?.docId,
      docId: item?.data?._id || item?.id || item?.docId,
    }));

    documents = documents.map(item =>
      convertToBriefingResourceReq(
        item,
        this.userKey,
        this.$route.name,
        this.getSelectedResource,
        documents,
        this.$i18n.locale,
        item?.writer ? item?.writer?.[0] : '',
        requestIdCreateReport,
        true
      )
    );

    await this.briefingService()
      .saveBriefingResource(
        documents.map(item => ({
          ...item,
          requestId: requestIdCreateReport,
          requestedResourceList: requestedResourceList,
          targetBpId: this.getSelectedResource?.bpId || null,
        })),
        false,
        'REPORT',
        this.reportTypes,
        this.getSelectedResource?.docId || this.getSelectedResource?._id || this.getSelectedResource?.originParentDocDetail?._id || '',
        this.getSelectedResource?.privateType ? this.getSelectedResource?.privateType : 'PUBLIC'
      )
      .then(async response => {
        if (response?.status === 200 && response?.data?.body?.data) {
          this.isLoading = false;

          setTimeout(() => {
            this.handleClosePopup();
          }, 1);
          setTimeout(() => {
            this.setShowDetailReport(false);
            this.$root.$emit('handleCloseModal');
          }, 5);

          this.$nextTick(() => {
            this.addBothLoadingReportCardAndLoadingReportToast(requestIdCreateReport, false);
          });
        } else {
          this.$bvToast.toast(`${this.$t('toast.saveReferenceFail')}`, {
            toaster: 'b-toaster-bottom-right',
            title: `${this.$t('toast.title.error')}`,
            variant: 'danger',
            solid: true,
            autoHideDelay: 5000,
          });

          this.isLoading = false;

          return;
        }
      })
      .catch(error => {
        console.log('Error save resource: ', error);
        this.$bvToast.toast(`${this.$t('toast.saveReferenceFail')}`, {
          toaster: 'b-toaster-bottom-right',
          title: `${this.$t('toast.title.error')}`,
          variant: 'danger',
          solid: true,
          autoHideDelay: 5000,
        });

        this.isLoading = false;

        return;
      });
  }

  // for report detail
  async callAPIUpdateReport() {
    if (
      !this.getStoreReferenceDocumentsForReport ||
      (this.getStoreReferenceDocumentsForReport && this.getStoreReferenceDocumentsForReport.length === 0)
    ) {
      return;
    }

    this.isLoading = true;

    const tempRequestId = 'UR'.concat('_' + new Date().toISOString()).concat('_' + this.userKey) || new Date().getTime().toString();
    const requestedResourceList = this.getStoreReferenceDocumentsForReport
      .filter(item => item?.data?._id || item?.id || item?.docId)
      .map(item => item?.data?._id || item?.id || item?.docId);
    let documents = this.getStoreReferenceDocumentsForReport.map(item => ({
      ...item.data,
      parentDocId: item?.data?._id || item?.id || item?.docId,
      docId: item?.data?._id || item?.id || item?.docId,
    }));

    documents = documents.map(item =>
      convertToBriefingResourceReq(
        item,
        this.userKey,
        this.$route.name,
        this.getSelectedResource,
        documents,
        this.$i18n.locale,
        item?.writer ? item?.writer?.[0] : '',
        tempRequestId,
        true
      )
    );

    await this.briefingService()
      .saveBriefingResource(
        documents.map(item => ({
          ...item,
          requestId: tempRequestId,
          requestedResourceList: requestedResourceList,
          targetBpId: this.getSelectedResource?.bpId || null,
        })),
        true,
        'REPORT',
        this.reportTypes,
        this.getSelectedResource?.docId || this.getSelectedResource?._id || this.getSelectedResource?.originParentDocDetail?._id || '',
        this.getSelectedResource?.privateType ? this.getSelectedResource?.privateType : 'PUBLIC'
      )
      .then(async response => {
        if (response?.status === 200 && response?.data?.body?.data) {
          this.isLoading = false;

          setTimeout(() => {
            this.handleClosePopup();
          }, 1);
          setTimeout(() => {
            this.setShowDetailReport(false);
            this.$root.$emit('handleCloseModal');
          }, 5);

          this.$nextTick(() => {
            // create new loading report data
            const tempToastReportData: ToastReportData = {
              id: '',
              requestId: tempRequestId.concat(this.loadingReportCardUtil.makeConcatStringByMediaType(this.getSelectedResource)),
              bpId: this.getSelectedResource?.bpId || '',
              addedTime: new Date().getTime(),
              status: REPORT_TOAST_STATUS.LOADING,
              statusCode: '',
              notifyReportData: '',
              isReShow: false,
              type: this.reportTypes[0],
              reportTypesResult: this.reportTypes,
              reportTypesResultForBriefing: [],
            };

            if (this.getToastReportData?.length > 0) {
              // console.log('logSetToastReportData_3526974801');

              this.reportToastUtil.setLoadingDataReportToastListLocalStorage([...this.getToastReportData, tempToastReportData]);
              this.setToastReportData([...this.getToastReportData, tempToastReportData]);
            } else {
              // console.log('logSetToastReportData_6847130592');

              this.reportToastUtil.setLoadingDataReportToastListLocalStorage([tempToastReportData]);
              this.setToastReportData([tempToastReportData]);
            }
          });
        } else {
          this.$bvToast.toast(`${this.$t('toast.saveReferenceFail')}`, {
            toaster: 'b-toaster-bottom-right',
            title: `${this.$t('toast.title.error')}`,
            variant: 'danger',
            solid: true,
            autoHideDelay: 5000,
          });

          this.isLoading = false;

          return;
        }
      })
      .catch(error => {
        console.log('Error save resource: ', error);
        this.$bvToast.toast(`${this.$t('toast.saveReferenceFail')}`, {
          toaster: 'b-toaster-bottom-right',
          title: `${this.$t('toast.title.error')}`,
          variant: 'danger',
          solid: true,
          autoHideDelay: 5000,
        });

        this.isLoading = false;

        return;
      });
  }

  // for briefing
  async updateReportsForBriefing() {
    if (this.getReferenceDocuments?.length > 0) {
      try {
        this.isLoading = true;

        const briefingResources = [];
        const requestGOA = 'GOA'.concat('_' + new Date().toISOString());
        this.getReferenceDocuments.forEach(item => {
          briefingResources.push(
            convertToBriefingResourceReq(
              item,
              this.$store.getters.userIdentity.id,
              this.$route.name,
              this.getSelectedBriefingPage || this.$store.getters['briefingStore/getSelectedBriefingPage'],
              this.getReferenceDocuments,
              this.$i18n.locale,
              this.$store.getters?.userIdentity?.lastName,
              requestGOA,
              true
            )
          );
        });

        await this.briefingService()
          .saveBriefingResource(briefingResources, false, 'REPORT', this.reportTypes)
          .then(async response => {
            if (response?.data?.body?.data && response?.data?.body?.data.length > 0 && response?.data?.body?.result === 'success') {
              await this.saveBriefing(requestGOA);
            } else {
              this.isLoading = false;
              this.showResponseMessage(`${this.$t('toast.update.fail')}`, `${this.$t('toast.title.error')}`, 'danger', 5000);
            }
          })
          .catch(err => {
            this.isLoading = false;
            this.showResponseMessage(`${this.$t('toast.update.fail')}`, `${this.$t('toast.title.error')}`, 'danger', 5000);
          });
      } catch (e) {
        this.isLoading = false;
        // console.log('Update Reports Failed with error: ', e);
        this.showResponseMessage(`${this.$t('toast.update.fail')}`, `${this.$t('toast.title.error')}`, 'danger', 5000);
      }
    } else {
      this.isLoading = false;
      return;
    }
  }

  // update briefing report type after go-over-again
  async saveBriefing(requestGOAInput: string) {
    const isUseStateInDetailPage = this.getSelectedBriefingPage?.bpId && this.$route?.name === 'MyBriefing-Detail';

    if (this.getSelectedBriefingPage?.bpId && this.userKey) {
      try {
        const requestId = 'UB-' + Date.now().toString();

        const tempTitle = removeUnUsedSpace(this.getSelectedBriefingPage?.briefingPageName || '');
        const tempDescription = removeUnUsedSpace(this.getSelectedBriefingPage?.briefingDescription || '');
        const privacy = this.getSelectedBriefingPage?.privacy || 'PRIVATE';

        await this.briefingService()
          .callAPIUpdateBriefingPage(
            requestId,
            this.userKey,
            this.getSelectedBriefingPage?.bpId,
            tempTitle,
            tempDescription,
            privacy,
            this.reportTypes
          )
          .then(res => {
            if (res?.data?.body?.body?.bpId && res?.data?.body?.status === 1) {
              handleRemoveLocalStorageGoOverAgainData([this.getSelectedBriefingPage?.bpId]);

              const tempUpdateListBriefingPages = this.getBriefingPages.map(item => {
                if (item.bpId === this.getSelectedBriefingPage?.bpId) {
                  return {
                    ...item,
                    briefingPageName: tempTitle,
                    briefingDescription: tempDescription,
                    privacy: privacy,
                    reportTypes: [this.formData.reportType],
                  };
                } else {
                  return item;
                }
              });

              this.setBriefingPages(tempUpdateListBriefingPages);
              if (isUseStateInDetailPage) {
                this.setSelectedBriefingPage({
                  ...this.getSelectedBriefingPage,
                  briefingPageName: tempTitle,
                  briefingDescription: tempDescription,
                  privacy: privacy,
                  reportTypes: [this.formData.reportType],
                });
              }

              // this.setIsShowSuccessUpdateBriefing(true);

              if (this.getIsAllowGoOverAgain === true) {
                this.setIsAllowGoOverAgain(!this.getIsAllowGoOverAgain);
              } else {
                this.setIsAllowGoOverAgain(false);
              }

              this.setWatchSetTempReferenceDocuments(!this.getWatchSetTempReferenceDocuments);

              this.isLoading = false;

              this.$nextTick(() => {
                this.addBothLoadingReportCardAndLoadingReportToast(requestGOAInput, true);

                setTimeout(() => {
                  this.handleClosePopup();
                }, 10);
              });
            } else {
              this.isLoading = false;
              this.showResponseMessage(`${this.$t('toast.update.fail')}`, `${this.$t('toast.title.error')}`, 'danger', 5000);
            }
          })
          .finally(() => {
            this.isLoading = false;
          });
      } catch (e) {
        this.isLoading = false;
        this.showResponseMessage(`${this.$t('toast.update.fail')}`, `${this.$t('toast.title.error')}`, 'danger', 5000);
      }
    } else {
      this.isLoading = false;
      this.showResponseMessage(`${this.$t('toast.update.fail')}`, `${this.$t('toast.title.error')}`, 'danger', 5000);
    }
  }

  async handleCallAPIToUpdateOrCreateReport() {
    switch (this.formData.updateOptions) {
      case 'CREATE':
        await this.callAPICreateNewReport();
        break;
      case 'UPDATE':
        await this.callAPIUpdateReport();
        break;
    }
  }

  handleClosePopup() {
    this.setIsShowReportSettingReportType(false);

    const button = document.getElementById('closePopupReportDetailSetting');

    if (button) {
      button.click();
    }
  }

  showResponseMessage(mess, title, variant, hideDelay) {
    this.$bvToast.toast(mess, {
      toaster: 'b-toaster-bottom-right',
      title: title,
      variant: variant,
      solid: true,
      autoHideDelay: hideDelay,
    });
  }

  addBothLoadingReportCardAndLoadingReportToast(requestIdInput: string, isGoOverAgain: boolean) {
    // create new loading report data for toast
    const tempToastReportData: ToastReportData = {
      id: '',
      requestId: requestIdInput.concat(this.loadingReportCardUtil.makeConcatStringByMediaType(this.getSelectedResource)),
      bpId: (isGoOverAgain === true ? this.getSelectedBriefingPage?.bpId : this.getSelectedResource?.bpId) || '',
      addedTime: new Date().getTime(),
      status: REPORT_TOAST_STATUS.LOADING,
      statusCode: '',
      notifyReportData: '',
      isReShow: false,
      type: this.reportTypes[0],
      reportTypesResult: this.reportTypes,
      reportTypesResultForBriefing: [],
    };

    // console.log('tempToastReportData: ', tempToastReportData);

    if (this.getToastReportData?.length > 0) {
      // console.log('logSetToastReportData_9371628054');

      this.reportToastUtil.setLoadingDataReportToastListLocalStorage([...this.getToastReportData, tempToastReportData]);
      this.setToastReportData([...this.getToastReportData, tempToastReportData]);
    } else {
      // console.log('logSetToastReportData_4059237816');

      this.reportToastUtil.setLoadingDataReportToastListLocalStorage([tempToastReportData]);
      this.setToastReportData([tempToastReportData]);
    }

    // create new loading report data for card
    const loadingDataReportCard: LoadingDataReportCard = {
      id: undefined,
      requestId: requestIdInput.concat('_' + LOADING_DATA_TYPE.REPORT.toString()),
      bpId: (isGoOverAgain === true ? this.getSelectedBriefingPage?.bpId : this.getSelectedResource?.bpId) || '',
      addedTime: new Date().getTime(),
      type: '',
    };

    this.loadingReportCardUtil.addLoadingCardToLocalStorage(loadingDataReportCard);
  }
  get isAvailableIndepthReport() {
    if (!this.account || !this.account.policyUsers) return true;

    for (const paidInfo of this.account.policyUsers) {
      if (paidInfo.featureIdx === 2 && paidInfo.remain === 0) {
        return false;
      }
    }
    return true;
  }
}
